import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  ChevronRight,
  LogIn,
  Pencil,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/layout/PageHeader";
import { OverviewToolbar } from "../../components/layout/OverviewToolbar";
import {
  loadViewMode,
  saveViewMode,
  useFitsMultipleColumns,
  ViewToggle,
  type ViewMode,
} from "../../components/layout/ViewToggle";
import {
  customersAdminApi,
  type AdminCustomerListItem,
} from "../../api/customersAdminApi";
import { useSession } from "../../hooks/useSession";
import { useBrand } from "../../components/brand/BrandProvider";

const CARD_MIN = 280;

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: AdminCustomerListItem[] }
  | { status: "error"; message: string };

/**
 * Admin-only customers overview. Backed by dbo.spAdminCustomerListGet
 * via /api/admin/customers and matches the cards-or-rows pattern of
 * the Huntgroups / Destinations overviews so the navigation feels
 * coherent when an admin moves around the SPA.
 *
 * Each tile exposes two actions:
 *   - Edit (placeholder, will land on /admin/customers/:id later)
 *   - Switch to (impersonate) which re-authenticates the session as
 *     that customer and lands on /dashboard.
 */
export function CustomersAdminPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewMode>(() => loadViewMode("admin-customers"));
  const [switchingId, setSwitchingId] = useState<number | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const canShowToggle = useFitsMultipleColumns(listRef, CARD_MIN);
  const navigate = useNavigate();
  const { setSession } = useSession();
  const { refresh: refreshBrand } = useBrand();

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as AdminCustomerListItem[];
    const q = search.trim().toLowerCase();
    if (!q) return state.items;
    return state.items.filter(
      (c) =>
        c.customerName.toLowerCase().includes(q) ||
        c.managerName.toLowerCase().includes(q) ||
        (c.debtorName ?? "").toLowerCase().includes(q),
    );
  }, [state, search]);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .list()
      .then((response) => {
        if (!active) return;
        setState({ status: "ready", items: response.items ?? [] });
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : "Failed to load customers";
        setState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, []);

  const handleEdit = (customerId: number) => {
    navigate(`/admin/customers/${customerId}`);
  };

  const handleSwitch = async (customerId: number) => {
    setSwitchError(null);
    setSwitchingId(customerId);
    try {
      const newSession = await customersAdminApi.switchTo(customerId);
      // Push the new (Customer-kind) session into the global context
      // so the sidebar / topbar / banner reflect the new identity
      // immediately, then re-fetch the brand because the resolved
      // primary colour follows the impersonated customer's manager.
      setSession(newSession?.isAuthenticated ? newSession : null);
      try {
        await refreshBrand();
      } catch {
        // brand refresh is cosmetic; never block the navigation.
      }
      navigate("/dashboard");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to switch to customer";
      setSwitchError(message);
      setSwitchingId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Customers"
        actions={
          <Link
            to="/admin/customers/new"
            aria-label="New customer"
            className="btn btn-primary"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
          >
            <Plus size={14} /> New customer
          </Link>
        }
      />
      <div className="dashboard">
        {switchError && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <AlertTriangle size={18} />
            <div>{switchError}</div>
          </div>
        )}
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading customers…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Something went wrong.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
          </div>
        )}
        {state.status === "ready" && state.items.length === 0 && <EmptyState />}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by customer, manager or debtor"
              count={{ matched: filtered.length, total: state.items.length }}
              trailing={
                canShowToggle ? (
                  <ViewToggle
                    value={view}
                    onChange={(next) => {
                      setView(next);
                      saveViewMode("admin-customers", next);
                    }}
                  />
                ) : null
              }
            />
            <div ref={listRef}>
              {filtered.length === 0 ? (
                <div className="tile" style={{ color: "var(--portal-muted)" }}>
                  No customers match "{search}".
                </div>
              ) : view === "card" || !canShowToggle ? (
                <CustomersCardGrid
                  items={filtered}
                  switchingId={switchingId}
                  onSwitch={handleSwitch}
                  onEdit={handleEdit}
                />
              ) : (
                <CustomersRowList
                  items={filtered}
                  switchingId={switchingId}
                  onSwitch={handleSwitch}
                  onEdit={handleEdit}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <Building2 size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>No customers</h3>
        <p style={{ marginTop: 0 }}>There are no customers to manage yet.</p>
      </div>
    </section>
  );
}

interface RowActionProps {
  items: AdminCustomerListItem[];
  switchingId: number | null;
  onSwitch: (customerId: number) => void;
  onEdit: (customerId: number) => void;
}

/**
 * Status pill — green for enabled, neutral grey for disabled.
 * Lives at module scope so card + row variants share the same look.
 */
function StatusChip({ enabled }: { enabled: boolean }) {
  const styles: React.CSSProperties = enabled
    ? {
        background: "var(--portal-status-success-soft)",
        color: "var(--portal-status-success-darker)",
        borderColor: "var(--portal-status-success)",
      }
    : {
        background: "var(--portal-surface)",
        color: "var(--portal-muted)",
        borderColor: "var(--portal-border)",
      };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid",
        fontSize: "0.8rem",
        fontWeight: 600,
        ...styles,
      }}
    >
      {enabled ? "Enabled" : "Disabled"}
    </span>
  );
}

function CustomersCardGrid({ items, switchingId, onSwitch, onEdit }: RowActionProps) {
  return (
    <div
      className="tile-cards"
      style={{ ["--tile-min" as never]: `${CARD_MIN}px` }}
    >
      {items.map((c) => {
        const busy = switchingId === c.customerId;
        return (
          <article
            key={c.customerId}
            className="tile"
            style={{ gap: 12, padding: 20 }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={c.customerName}
            >
              {c.customerName || "—"}
            </div>
            <div>
              <StatusChip enabled={c.enabled} />
            </div>
            <div
              style={{
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={c.managerName}
            >
              {c.managerName || "—"}
            </div>
            <div
              style={{
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
              }}
            >
              {c.numbers} {c.numbers === 1 ? "number" : "numbers"} ·{" "}
              {c.logins} {c.logins === 1 ? "login" : "logins"}
            </div>
            <div
              style={{
                marginTop: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => onEdit(c.customerId)}
                title="Edit this customer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "0.85rem",
                }}
              >
                <Pencil size={14} /> Edit
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onSwitch(c.customerId)}
                disabled={busy || !c.enabled}
                title={
                  c.enabled
                    ? "Sign in as this customer"
                    : "This customer is disabled and cannot be impersonated."
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "0.85rem",
                }}
              >
                <LogIn size={14} />
                {busy ? "Switching…" : "Switch to"}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function CustomersRowList({ items, switchingId, onSwitch, onEdit }: RowActionProps) {
  return (
    <div className="tile-rows">
      {items.map((c) => {
        const busy = switchingId === c.customerId;
        return (
          <div
            key={c.customerId}
            className="tile tile-row"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
              padding: "12px 18px",
            }}
          >
            <div
              className="tile-row-title"
              style={{
                flex: "2 1 0",
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={c.customerName}
            >
              {c.customerName || "—"}
            </div>
            <div style={{ flexShrink: 0 }}>
              <StatusChip enabled={c.enabled} />
            </div>
            <div
              className="tile-row-detail"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={c.managerName}
            >
              {c.managerName || "—"}
            </div>
            <div
              className="tile-row-detail"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {c.numbers} numbers · {c.logins} logins
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onEdit(c.customerId)}
              title="Edit this customer"
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "0.85rem",
              }}
            >
              <Pencil size={14} /> Edit
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onSwitch(c.customerId)}
              disabled={busy || !c.enabled}
              title={
                c.enabled
                  ? "Sign in as this customer"
                  : "This customer is disabled and cannot be impersonated."
              }
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "0.85rem",
              }}
            >
              <LogIn size={14} />
              {busy ? "Switching…" : "Switch to"}
              <ChevronRight size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
